import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchApi, meetingNotesApi } from '../lib/api';

export default function Dashboard({ onNavigate }) {
  const { profile, role, hasSubscription, hasEventAccess, eventSettings } = useAuth();
  const [wishlistEmpty, setWishlistEmpty] = useState(false);
  const [insights, setInsights] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (role === 'company' || role === 'admin') {
      fetchApi('/api/company-profile/wishlist')
        .then(res => {
          const w = res.wishlist || {};
          setWishlistEmpty(!w.categories_seeking?.length && !w.age_ranges_seeking?.length && !w.mechanics_seeking?.length);
        })
        .catch(() => {});
    }
    if (hasSubscription || role === 'admin') {
      meetingNotesApi.getInsights()
        .then(res => {
          const all = res.insights || [];
          setInsights(all);
          setUnreadCount(all.filter(i => !i.is_read).length);
        })
        .catch(() => {});
    }
  }, [role, hasSubscription]);

  const firstName = profile?.display_name?.split(' ')[0] || 'there';
  // [EVENT-MODE] Simplified dashboard for all roles
  const roleWelcome = {
    inventor: 'Your invention intelligence hub. Track ideas, discover trends, and pitch smarter.',
    company: 'Manage your submission pipeline and discover trending inventor capabilities.',
    admin: 'Full platform access. Manage events, pipeline, and inventor matching.',
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-vault-navy">Welcome back, {firstName}</h1>
        <p className="text-[14px] text-gray-400 mt-2">{roleWelcome[role] || roleWelcome.inventor}</p>
        {eventSettings?.event_name && (
          <div className="mt-3 px-3 py-2 rounded-btn bg-amber-50 border border-vault-gold/20 inline-block">
            <span className="text-sm font-semibold text-vault-gold">{eventSettings.event_name}</span>
          </div>
        )}
      </div>

      {(() => {
        const pitchWindowInsights = insights.filter(i => i.overlaps?.some(o => o.pitch_window));
        return pitchWindowInsights.length > 0 ? (
          <div className="mb-6">
            <h2 className="font-heading text-lg text-vault-navy mb-3 flex items-center gap-2">
              Act Now
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-amber-700 bg-amber-100">{pitchWindowInsights.length}</span>
            </h2>
            <div className="space-y-3">
              {pitchWindowInsights.slice(0, 3).map(insight => {
                const pwOverlap = insight.overlaps.find(o => o.pitch_window);
                const companyName = insight.meeting_notes?.companies?.name || 'Unknown';
                return (
                  <div key={insight.id}
                    className="px-4 py-3 rounded-glass border-2 border-amber-400/50 bg-amber-50/30 cursor-pointer hover:shadow-glass-hover transition-all"
                    onClick={() => onNavigate?.('notes')}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-amber-700">Pitch Window Open</span>
                      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    </div>
                    <p className="text-sm text-vault-navy">
                      <span className="font-semibold">{companyName}</span>
                      {' -- '}
                      {[
                        pwOverlap?.idea_title,
                        pwOverlap?.category,
                        pwOverlap?.signal
                      ].filter(Boolean).join(' / ') || 'matching signal detected'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{insight.next_step}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div className="glass p-6">
          <div className="text-[13px] font-semibold text-gray-400 mb-1">
            {role === 'company' || role === 'admin' ? 'Submissions' : 'Ideas in Vault'}
          </div>
          <div className="font-heading text-4xl text-vault-navy">0</div>
          <div className="text-[12px] text-gray-400 mt-2">Get started by adding your first entry</div>
        </div>

        <div className="glass p-6">
          <div className="text-[13px] font-semibold text-gray-400 mb-1">Trend Alerts</div>
          <div className="font-heading text-4xl text-vault-navy">0</div>
          <div className="text-[12px] text-gray-400 mt-2">Trends will appear as they're detected</div>
        </div>

        <div className="glass p-6">
          <div className="text-[13px] font-semibold text-gray-400 mb-1">
            {role === 'company' || role === 'admin' ? 'Pipeline Items' : 'Active Pitches'}
          </div>
          <div className="font-heading text-4xl text-vault-navy">0</div>
          <div className="text-[12px] text-gray-400 mt-2">Coming soon</div>
        </div>
      </div>

      {insights.length > 0 && (
        <div className="glass p-6 mt-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-vault-navy text-sm flex items-center gap-2">
              AI Insights
              {unreadCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-vault-gold text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </h3>
            <button onClick={() => onNavigate?.('notes')} className="text-xs font-semibold text-vault-gold hover:underline">
              View All -&gt;
            </button>
          </div>
          <div className="space-y-2">
            {insights.slice(0, 3).map(insight => (
              <div key={insight.id} className="flex items-start gap-3 py-2 border-b border-vault-border last:border-0">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-vault-navy">{insight.meeting_notes?.companies?.name}</span>
                    {!insight.is_read && <span className="w-1.5 h-1.5 rounded-full bg-vault-gold" />}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{insight.summary?.split('bullet')[0]?.trim() || insight.next_step}</p>
                </div>
                <span className="text-[10px] text-gray-300 shrink-0">{new Date(insight.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event banner */}
      {hasEventAccess && (
        <div className="glass p-5 mt-5 border-2 border-emerald-200 bg-emerald-50/30">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-vault-navy text-sm">Active Event Registration</h3>
              <p className="text-xs text-gray-400 mt-0.5">You have an active event -- view your dashboard.</p>
            </div>
            <button onClick={() => onNavigate?.('events')} className="text-sm font-semibold text-vault-gold hover:underline">
              Go to Events ->
            </button>
          </div>
        </div>
      )}

      {/* Company wishlist CTA */}
      {(role === 'company' || role === 'admin') && wishlistEmpty && (
        <div className="glass p-5 mt-5 border-2 border-vault-gold/30 bg-amber-50/30">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-vault-navy text-sm">Set Up Your Wish List</h3>
              <p className="text-xs text-gray-400 mt-0.5">Tell our AI what you're looking for so inventors can be matched to your company.</p>
            </div>
            <button onClick={() => onNavigate?.('wishlist')} className="px-4 py-2 rounded-btn text-sm font-semibold text-white shrink-0" style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}>
              Set Up Wish List
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
